import * as Sentry from "@sentry/browser";
import { get, omitBy } from "lodash";
import { warningToast } from "@canopytax/toast-service";
import {
  getAssociatedService,
  getSquad,
  getVersioningInformation,
  getFeatureToggleInfo,
} from "./error.helpers.js";
import { getHashFromManifestEntry } from "./single-spa.helpers.js";

function getWhiteListUrls() {
  return [
    "canopy.ninja",
    "canopy.dev",
    "canopytax.com",
    "localhost",
    "ielocal",
    // white label domains
    "clientportal.ninja",
    "clientportal.com",
  ];
}

let appIsUnloading = false;

export function beforeSend(event, hint) {
  if (appIsUnloading) {
    return null; // Skip errors that occur while the app is unloading (or reloading)
  }

  // The following is added because of an issue with 3rd party scripts throwing
  // empty strings on the login page for windows 10 chrome users which sentry
  // is unable to handle. https://github.com/getsentry/sentry-javascript/issues/3440
  if (
    event !== undefined &&
    event.exception !== undefined &&
    event.exception.values !== undefined &&
    event.exception.values.length > 0
  ) {
    var e = event.exception.values[0];
    if (
      e.type === "UnhandledRejection" &&
      e.value.includes("Non-Error promise rejection captured with value:")
    ) {
      return null;
    }
  }

  try {
    const { exception } = event;
    const frames = get(exception, "values[0].stacktrace.frames");

    const errorUrl = frames ? frames[frames.length - 1].filename : "";
    let error = hint.originalException;
    if (typeof error === "string") error = new Error(error);

    if (
      errorUrl &&
      !getWhiteListUrls().find((matcher) => errorUrl.includes(matcher))
    )
      return null; // the error occurred from an unknown domain

    // eslint-disable-next-line canopy/no-window-auth-globals -- logging lib must not import cp-client-auth (circular dep); runs in error path.
    event = addErrorData(event, error, window.loggedInUser);
  } catch (resolutionError) {
    console.error("Error resolving error: ", resolutionError);
  }

  return Promise.resolve(event);
}

function addErrorData(event, error, loggedInUser = {}) {
  // The ESM index page publishes esm_manifest and the SystemJS one publishes sofe_manifest.
  // A page only ever loads one of the two builds, so whichever global is present describes the
  // bundles this error actually came from.
  const manifest = omitBy(
    window.esm_manifest || window.sofe_manifest || {},
    (val, key) => {
      return key.includes("/!sofe") || !val.endsWith(".js");
    }
  );
  if (error && error.showToast) {
    const toastBreadcrumb = {
      category: "console",
      level: "error",
      message: `WARNING-TOAST: ${error.toastMessage}`,
      timestamp: Date.now() / 1000,
    };

    event = {
      ...event,
      breadcrumbs: [...(event.breadcrumbs || []), toastBreadcrumb],
    };
  }

  if (error) {
    const service = error.service
      ? error.service
      : getAssociatedService(event.exception, manifest);
    const versions = getVersioningInformation(manifest, service);
    const toggles = getFeatureToggleInfo();
    // Per-service `version.<svc>` tags carry just the build hash —
    // compact and filterable in Sentry's UI for cross-service correlation.
    // The full manifest URL for each service is in the `versions` extra.
    const versionTags = Object.fromEntries(
      Object.entries(versions).map(([svc, info]) => [
        `version.${svc}`,
        getHashFromManifestEntry(info),
      ])
    );
    const ft_crm =
      // eslint-disable-next-line canopy/no-window-auth-globals -- logging lib must not import cp-client-auth (circular dep); runs in error path.
      window.tenant?.crm_status === "crm_hierarchy_complete" && toggles?.ft_crm;

    event = {
      ...event,
      tags: {
        ...(event.tags || {}),
        ...(error.tags || {}),
        ...versionTags,
        ft_crm: ft_crm,
        toastDisplayed: error.showToast,
        toastMessage: error.toastMessage,
        ...(error.status ? { "error.status": String(error.status) } : {}),
        service: service,
        squad: getSquad(service),
        userName: loggedInUser.name,
        userEmail: loggedInUser.email,
        userRole: loggedInUser.role,
      },
      extra: {
        ...(event.extra || {}),
        ...(error.extra || {}),
        toastMessage: error.toastMessage,
        toggles: toggles,
        versions: versions,
        // eslint-disable-next-line canopy/no-window-auth-globals -- logging lib must not import cp-client-auth (circular dep); runs in error path.
        betas: window.betas,
      },
    };
  }

  return event;
}

export function handleToaster(error) {
  if (error && error.showToast !== false) {
    error.showToast = true;
    error.toastMessage =
      error.toastMessage ||
      get(error, "data.errors.userMessage") ||
      `Something went wrong... We're working fast to fix it!`;
    warningToast(error.toastMessage);
  }
  return error;
}

function updateOnlineStatus() {
  Sentry.addBreadcrumb({
    category: "connection",
    message:
      "Online status changed: " + (navigator.onLine ? "online" : "offline"),
    level: "info",
  });
}

window.addEventListener("online", updateOnlineStatus);
window.addEventListener("offline", updateOnlineStatus);

window.addEventListener("beforeunload", function () {
  appIsUnloading = true;
});
